import React, { useEffect, useState, useRef } from "react";
import { functions } from "../../../../database/firebase";

export default function PrivatePdfUpload({ communityId, productId, saved }: { communityId: string; productId: string; saved: boolean }) {
  const generation = useRef(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<any>(null);
  useEffect(() => {
    let active = true;
    generation.current++;
    setBusy(false);
    setStatus(null); setMessage("");
    if (saved) functions.httpsCallable("getShopProductDeliveryStatus")({ communityId, productId })
      .then(result => { if (active) setStatus(result.data); })
      .catch(() => { if (active) setMessage("Could not load private delivery status."); });
    return () => { active = false; generation.current++; };
  }, [communityId, productId, saved]);
  async function upload(file?: File) {
    if (!file) return;
    // The server checks the file content; the browser MIME type is not trusted either way.
    if (!/\.(pdf|epub|zip)$/i.test(file.name) || !file.size || file.size > 7 * 1024 * 1024) {
      setMessage("Choose a PDF, EPUB or ZIP file up to 7 MB. Larger files, up to 64 MB, can be sent through an upload link from your Tribed assistant."); return;
    }
    const requestId = generation.current;
    setBusy(true); setMessage("");
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader(); reader.onerror = () => reject(new Error("read_failed"));
        reader.onload = () => resolve(String(reader.result).split(",")[1]); reader.readAsDataURL(file);
      });
      const result = await functions.httpsCallable("uploadShopProductPdf")({ communityId, productId, base64, fileName: file.name, contentType: file.type || "" });
      if (generation.current !== requestId) return;
      setStatus(result.data); setMessage("File attached privately. Delivery is saved immediately; visibility and price are unchanged.");
    } catch (error) {
      // Callable HttpsErrors carry the server's reason (for example "The file is not a valid EPUB ..."); anything else gets the generic text.
      const code = String((error as any)?.code || ""), reason = String((error as any)?.message || "");
      if (generation.current === requestId) setMessage(code && !/internal$/.test(code) && reason ? reason : "The file could not be attached. Check the file and community access, then retry. Reload the product if someone else changed its delivery.");
    }
    finally { if (generation.current === requestId) setBusy(false); }
  }
  return <fieldset style={{ margin: "16px 0", padding: 16 }} disabled={!saved || busy}>
    <legend>Private file delivery (PDF, EPUB or ZIP)</legend>
    <p>{status?.hasProtectedDelivery ? `Attached: ${status.fileName || "private file"}` : status?.externalDelivery ? "An external delivery link is configured. Uploading replaces it." : "No private file attached."}</p>
    <label htmlFor={`pdf-${productId}`}>{status?.hasProtectedDelivery ? "Replace file" : "Upload file"}</label>{" "}
    <input id={`pdf-${productId}`} type="file" accept=".pdf,.epub,.zip,application/pdf,application/epub+zip,application/zip" onChange={e => { const file = e.target.files?.[0]; e.target.value = ""; void upload(file); }} />
    {!saved && <p>Save this product first, then edit it to attach its file.</p>}
    <p>PDF, EPUB or ZIP, up to 7 MB. Larger files, up to 64 MB, can be sent through an upload link from your Tribed assistant. Attaching saves immediately, replaces the existing delivery and removes an external item link. Canceling product edits does not undo an attached file. It does not publish the product. Buyers receive a 10-minute download link after their community access is verified. Native sales still need a RevenueCat product.</p>
    {busy && <p role="status">Uploading file…</p>}
    {message && <p role="status">{message}</p>}
  </fieldset>;
}
